import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import { RequestParams } from '@src/app/shared/types/common';
import { languages } from '@src/app/constants/languages';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  private currentLanguageSubject = new BehaviorSubject<Record<string, any>>({});
  currentLanguage$: Observable<Record<string, any>> =
    this.currentLanguageSubject.asObservable();

  private languagesSubject = new BehaviorSubject<any[]>(languages);
  languages$: Observable<any[]> = this.languagesSubject.asObservable();

  private menuSubject = new BehaviorSubject<any[]>([]);
  menu$: Observable<any[]> = this.menuSubject.asObservable();

  private productsMenuSubject = new BehaviorSubject<any[]>([]);
  productsMenu$: Observable<any[]> = this.productsMenuSubject.asObservable();

  private productsSubject = new BehaviorSubject<any[]>([]);
  products$: Observable<any[]> = this.productsSubject.asObservable();

  private activeProductSubject = new BehaviorSubject<Record<string, any>>({});
  activeProduct$: Observable<Record<string, any>> =
    this.activeProductSubject.asObservable();

  private mainNavSubject = new BehaviorSubject<any[]>([]);
  mainNav$: Observable<any[]> = this.mainNavSubject.asObservable();

  private investNavSubject = new BehaviorSubject<any[]>([]);
  investNav$: Observable<any[]> = this.investNavSubject.asObservable();

  private selectLandingNavSubject = new BehaviorSubject<any[]>([]);
  selectLandingNav$: Observable<any[]> =
    this.selectLandingNavSubject.asObservable();

  private menuStaticSubject = new BehaviorSubject<any[]>([]);
  menuStatic$: Observable<any[]> = this.menuStaticSubject.asObservable();

  private categoryTypesListSubject = new BehaviorSubject<any[]>([]);
  categoryTypesList$: Observable<any[]> =
    this.categoryTypesListSubject.asObservable();

  private menuContentSubject = new BehaviorSubject<Record<string, any>>({});
  menuContent$: Observable<Record<string, any>> =
    this.menuContentSubject.asObservable();

  private menuInvestSubject = new BehaviorSubject<Record<string, any>>({});
  menuInvest$: Observable<Record<string, any>> =
    this.menuInvestSubject.asObservable();

  private menuSelectLandingSubject = new BehaviorSubject<Record<string, any>>(
    {},
  );
  menuSelectLanding$: Observable<Record<string, any>> =
    this.menuSelectLandingSubject.asObservable();

  private specialOffersListSubject = new BehaviorSubject<Record<string, any>[]>(
    [],
  );
  specialOffersList$: Observable<Record<string, any>[]> =
    this.specialOffersListSubject.asObservable();

  constructor(private apiService: ApiService) {}

  get isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  get languages(): any {
    return this.languagesSubject.value;
  }

  get currentLanguage(): any {
    return this.currentLanguageSubject.value;
  }

  get menu(): any[] {
    return this.menuSubject.value;
  }

  get productsMenu(): any[] {
    return this.productsMenuSubject.value;
  }

  get products(): any[] {
    return this.productsSubject.value;
  }

  get activeProduct(): Record<string, any> {
    return this.activeProductSubject.value;
  }

  get investNav(): any[] {
    return this.investNavSubject.value;
  }

  get mainNav(): any[] {
    return this.mainNavSubject.value;
  }

  get selectLandingNav(): any[] {
    return this.selectLandingNavSubject.value;
  }

  get menuContent(): Record<string, any> {
    return this.menuContentSubject.value;
  }

  get menuInvest(): Record<string, any> {
    return this.menuInvestSubject.value;
  }

  get menuStatic(): any[] {
    return this.menuStaticSubject.value;
  }

  get categoryTypesList(): any[] {
    return this.categoryTypesListSubject.value;
  }

  get menuSelectLanding(): Record<string, any> {
    return this.menuSelectLandingSubject.value;
  }

  get specialOffersList(): Record<string, any>[] {
    return this.specialOffersListSubject.value;
  }

  setIsLoading(value: boolean): void {
    this.isLoadingSubject.next(value);
  }

  setLanguages(list: any[]): void {
    this.languagesSubject.next(list);
  }

  setCurrentLanguage(lang: any): void {
    this.currentLanguageSubject.next(lang);
  }

  setMenu(list: any[]): void {
    this.menuSubject.next(list);
  }

  setProductsMenu(list: any[]): void {
    this.productsMenuSubject.next(list);
  }

  setProducts(list: any[]): void {
    this.productsSubject.next(list);
  }

  setActiveProduct(value: Record<string, any>): void {
    this.activeProductSubject.next(value);
  }

  setMainNav(list: any[]): void {
    this.mainNavSubject.next(list);
  }

  setInvestNav(list: any[]): void {
    this.investNavSubject.next(list);
  }

  setSelectLandingNav(list: any[]): void {
    this.selectLandingNavSubject.next(list);
  }

  setMenuStatic(list: any[]): void {
    this.menuStaticSubject.next(list);
  }

  setMenuInvest(list: any[]): void {
    this.menuInvestSubject.next(list);
  }

  setCategoryTypesList(list: any[]): void {
    this.categoryTypesListSubject.next(list);
  }

  setMenuContent(content: Record<string, any>): void {
    this.menuContentSubject.next(content);
  }

  setMenuSelectLanding(content: Record<string, any>): void {
    this.menuSelectLandingSubject.next(content);
  }

  setSpecialOffersList(content: Record<string, any>[]): void {
    this.specialOffersListSubject.next(content);
  }

  /**
   * @description Handling API call errors
   * @param {HttpErrorResponse} error
   */
  handleError(error: HttpErrorResponse): void {
    this.isLoadingSubject.next(false);

    if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  getLanguages({
    transferCache = true,
  }: {
    transferCache: boolean;
  }): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getLanguages,
      transferCache,
    });
  }

  getProductTypes(
    params: RequestParams,
    { transferCache }: { transferCache: boolean } = { transferCache: true },
  ): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getProductTypes,
      params,
      transferCache,
    });
  }

  getProductsMenu(
    params: RequestParams,
    { transferCache }: { transferCache: boolean } = { transferCache: true },
  ): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getProductsMenu,
      params,
      transferCache,
    });
  }

  getSpecialOffersList(
    params: RequestParams,
    { transferCache }: { transferCache: boolean } = { transferCache: true },
  ): Observable<Record<string, any>[]> {
    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getSpecialOffersList,
      params,
      transferCache,
    });
  }

  getMenu(
    params: RequestParams,
    { transferCache }: { transferCache: boolean } = { transferCache: true },
  ): Observable<any[]> {
    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getMenu,
      params,
      transferCache,
    });
  }

  getCategoryTypesList(
    params: RequestParams,
  ): Observable<Record<number, any[]>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCategoryTypesList,
      params,
    });
  }

  postConvertExcel(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.convertExcel,
      params,
      body,
    });
  }
}
